import React, { useState, useEffect } from 'react';
import { getNews } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Newspaper, Calendar, Clock, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const NewsListPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await getNews();
        setNews(res.data.filter(n => n.is_published));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  const handleOpen = (item) => {
    setSelectedNews(item);
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="news-list-page">
      {/* Header */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-4">
            <span className="text-primary">News</span> & Aktuelles
          </h1>
          <p className="text-text-secondary max-w-2xl">
            Neuigkeiten und Ankündigungen aus dem Vereinsleben.
            Klicke auf eine News, um mehr zu erfahren.
          </p>
        </div>
      </section>

      {/* News List */}
      <section className="section-padding">
        <div className="container-max">
          <div className="space-y-6">
            <h2 className="text-xl font-heading font-bold uppercase">Alle Neuigkeiten</h2>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="card-surface animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-6 bg-white/10 rounded w-1/2 mb-2" />
                      <div className="h-4 bg-white/10 rounded w-3/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : news.length > 0 ? (
              <div className="space-y-4">
                {news.map((item) => (
                  <Card
                    key={item.news_id}
                    className="card-surface hover:border-primary/30 transition-all cursor-pointer"
                    onClick={() => handleOpen(item)}
                    data-testid={`news-card-${item.news_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          {item.image_url ? (
                            <img
                              src={getPhotoUrl(item.image_url)}
                              alt={item.title}
                              className="w-14 h-14 object-cover rounded-sm flex-shrink-0"
                            />
                          ) : (
                            <div className="w-14 h-14 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                              <Newspaper className="w-6 h-6 text-primary" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Calendar className="w-4 h-4 text-text-secondary" />
                              <span className="text-sm text-text-secondary">{formatDate(item.created_at)}</span>
                            </div>
                            <h3 className="font-heading font-bold uppercase text-lg mb-1">{item.title}</h3>
                            <p className="text-sm text-text-secondary line-clamp-2">
                              {item.excerpt || item.content}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 ml-4 flex-shrink-0">
                          <Badge className="bg-primary/20 text-primary border-primary/30">
                            <Newspaper className="w-3 h-3 mr-1" />
                            News
                          </Badge>
                          <span className="text-primary text-sm flex items-center gap-1">
                            Lesen <ArrowRight className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="card-surface">
                <CardContent className="p-6 text-center">
                  <Newspaper className="w-12 h-12 text-text-secondary mx-auto mb-4" />
                  <p className="text-text-secondary">Keine News vorhanden.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* News Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              {selectedNews?.title}
            </DialogTitle>
            <DialogDescription className="text-text-secondary flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {selectedNews && formatDate(selectedNews.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedNews && (
            <div className="space-y-4 mt-4">
              {selectedNews.image_url && (
                <img
                  src={getPhotoUrl(selectedNews.image_url)}
                  alt={selectedNews.title}
                  className="w-full max-h-[300px] object-cover rounded-sm"
                />
              )}

              <div className="card-elevated p-4 whitespace-pre-line text-sm leading-relaxed text-text-secondary">
                {selectedNews.content}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsListPage;
